import type { MonitorCreationProposal } from '@repo/api-codegen';
import axios from 'axios';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { api } from '../../../network';
import type { IEmbeddedPromptResponseStep, IUser } from '../../api';
import { authHeaders } from '../../api/common';
import { AiChatStore } from '../../features/AiChat/store';
import { uploadFile } from '../RichEditor/RichEditor.utils';
import type { AIAssistantCurrentPage } from './types';

export function isSecodaAIUser(
	user: Pick<IUser, 'email'>,
	workspace_id: string
) {
	// regex match this pattern "secoda-ai-{workspace_id}@secoda.co"
	const pattern = `secoda-ai-${workspace_id}@secoda\.co`;
	return (user.email ?? '').match(pattern);
}

// This function is a helper for the drag and drop + clipboard paste functionality
export async function uploadFileTransferItemsAsAttachments(
	items: DataTransferItem[],
	store: AiChatStore
) {
	const files = items
		.filter((item) => item.kind === 'file' && item.getAsFile())
		.map((item) => item.getAsFile() as File);
	await uploadFilesAsAttachments(files, store);
}

export async function uploadFilesAsAttachments(
	files: File[],
	store: AiChatStore
) {
	await Promise.all(
		files.map(async (file) => {
			const result = await uploadFile(file, false, true);
			store.uploadAttachment({ url: result, filename: file.name });
		})
	);
}

export async function generatePromptMessage(
	value: string,
	currentPage?: AIAssistantCurrentPage,
	attachments?: string[]
) {
	let message = value;

	if (attachments?.length) {
		const fileUrls = await Promise.all(
			attachments.map((url) => {
				const decodedPath = atob(url.replace('/download?path=', ''));
				return axios
					.get(`${api()}${decodedPath}`, authHeaders())
					.then(
						(response) => `<ATTACHED_FILE>(${response.data})</ATTACHED_FILE>`
					);
			})
		);
		message = `${fileUrls.join('\n')}\n${message}`;
	}

	if (!isNil(currentPage)) {
		return `<SECODA_AI_CONTEXT>[Secoda AI Context](${currentPage.type}/${currentPage.id})</SECODA_AI_CONTEXT>\n${message}`;
	}

	return message;
}

export function sanitizePromptMessage(value?: string | null) {
	if (!value) {
		return '';
	}
	return value
		.replace(/<SECODA_AI_CONTEXT>.*?<\/SECODA_AI_CONTEXT>/g, '')
		.replace(/<ATTACHED_FILE>.*?<\/ATTACHED_FILE>/g, '');
}

export function getAttachmentUrlsInPromptMessage(
	value?: string | null
): string[] {
	if (!value) {
		return [];
	}

	const attachmentRegex = /<ATTACHED_FILE>\((.*?)\)<\/ATTACHED_FILE>/g;
	const matches = [...value.matchAll(attachmentRegex)];
	return matches.map((match) => match[1]);
}

export function getFileNameFromUrl(url: string): string {
	try {
		const matches = url.match(/filename%3D(.*?)(%3B|;|&|$)/);
		if (matches && matches[1]) {
			return decodeURIComponent(matches[1]);
		}
		return 'Attached File';
	} catch (e) {
		return 'Attached File';
	}
}

export function getTimePeriod(dateString: string) {
	const day = dayjs(dateString);
	if (day.isToday()) return 'Today';
	if (day.isYesterday()) return 'Yesterday';
	if (day.isAfter(dayjs().subtract(7, 'day'))) return 'Previous 7 Days';
	if (day.isAfter(dayjs().subtract(30, 'day'))) return 'Previous 30 Days';
	return 'Older';
}

enum AIStepFormat {
	General = 'General',
	SQL = 'SQL',
}

export interface AIStepData {
	format: AIStepFormat;
	functionCall: string;
	definition: string;
	title: string;
}

export function extractStepData(step: IEmbeddedPromptResponseStep): AIStepData {
	const { functionCall } = step;

	if (functionCall.includes('run_sql')) {
		const sql = functionCall.substring(8, functionCall.length - 1);
		return {
			...step,
			format: AIStepFormat.SQL,
			functionCall: sql ?? step.functionCall,
		};
	}

	return { ...step, format: AIStepFormat.General };
}

const SECTION_HEADERS = [
	'ENTITY ANALYSIS',
	'RESOURCE MAPPING',
	'QUERY STRATEGY',
	'INSIGHT EXTRACTION',
];

export function toSentenceCase(text: string): string {
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export interface Section {
	title: string;
	subtitle?: string;
	points: { text: string; numbered: boolean }[];
	description?: string;
	afterPoints?: string;
}

export function parseAnalysisContent(content: string): Section[] {
	const lines = content
		.split(/\n|(?=[A-Z ]+:)/g)
		.map((l) => l.trim())
		.filter(Boolean);
	const sections: Section[] = [];
	let currentSection: Section | null = null;
	let currentDescription: string[] = [];
	let afterPointsText: string[] = [];
	let hasPoints = false;

	for (const line of lines) {
		// Check if this line is a section header
		const header = SECTION_HEADERS.find((h) =>
			line.toUpperCase().startsWith(h)
		);
		if (header) {
			// Start a new section
			if (currentSection) {
				if (currentDescription.length > 0) {
					currentSection.description = currentDescription.join(' ');
					currentDescription = [];
				}
				if (afterPointsText.length > 0) {
					currentSection.afterPoints = afterPointsText.join(' ');
					afterPointsText = [];
				}
				sections.push(currentSection);
			}
			currentSection = { title: header, points: [] };
			hasPoints = false;
			// If there's text after the header, add as description
			const afterHeader = line
				.slice(header.length)
				.trim()
				.replace(/^[:-]\s*/, '');
			if (afterHeader) currentDescription.push(afterHeader);
		} else if (currentSection) {
			// Check if line starts with a number or bullet point
			const isNumbered = /^\d+\./.test(line);
			const isBulleted = /^-/.test(line);

			if (isNumbered || isBulleted) {
				// If we have accumulated description, add it to the section
				if (currentDescription.length > 0) {
					currentSection.description = currentDescription.join(' ');
					currentDescription = [];
				}
				// Add as a point to the current section
				currentSection.points.push({
					text: line.replace(/^[-\d.]+\s*/, ''),
					numbered: isNumbered,
				});
				hasPoints = true;
			} else if (line) {
				// If we've seen points before, add to afterPoints
				if (hasPoints) {
					afterPointsText.push(line);
				} else {
					// Add to description
					currentDescription.push(line);
				}
			}
		}
	}

	if (currentSection) {
		if (currentDescription.length > 0) {
			currentSection.description = currentDescription.join(' ');
		}
		if (afterPointsText.length > 0) {
			currentSection.afterPoints = afterPointsText.join(' ');
		}
		sections.push(currentSection);
	}
	return sections;
}

export function getScheduleText(
	schedule: MonitorCreationProposal['data']['schedule']
): string {
	switch (schedule.cadence) {
		case 'hourly':
			return `Every ${schedule.frequency} hour${schedule.frequency > 1 ? 's' : ''}`;
		case 'daily':
			return `Daily at ${schedule.hour}:00 UTC`;
		case 'weekly': {
			const days = [
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
				'Sunday',
			];
			return `Weekly on ${days[schedule.day]} at ${schedule.hour}:00 UTC`;
		}
		default:
			return 'Invalid schedule';
	}
}
